import { Pill, PLACEMENT, ToolTip } from "@gadgetinc/widgets";
import { HelpIcon } from "@gadgetinc/widgets/src/icons/HelpIcon";
import { useStyletron } from "baseui";
import React from "react";
import type { StyleObject } from "styletron-react";
import { DocLink } from "./DocLink";

interface FrameworkVersionPillProps {
  version: `${number}.${number}.${number}+`;
  $style?: StyleObject;
}

/**
 * Displays a framework version requirement with an interactive tooltip.
 * The tooltip explains the version constraint to users.
 * Clicking the pill navigates to the framework version documentation.
 */
export const FrameworkVersionPill = (props: FrameworkVersionPillProps) => {
  const [css, $theme] = useStyletron();

  // Parse version to generate appropriate tooltip text
  const tooltipText = getVersionTooltipText(props.version);

  return (
    <ToolTip label={tooltipText} placement={PLACEMENT.top} inline>
      <DocLink href="/guides/gadget-framework" $style={{ display: "inline-flex" }}>
        <Pill
          $color="blue"
          $style={{
            marginLeft: $theme.sizing.scale200,
            verticalAlign: "middle",
            cursor: "pointer",
            ...props.$style,
          }}
          data-framework-version={props.version}
          aria-label={tooltipText}
          role="note"
        >
          <span className={css({ display: "inline-flex", alignItems: "center", gap: $theme.sizing.scale100 })}>
            Framework v{props.version}
            <HelpIcon size={12} color={$theme.colors.cyan700} aria-hidden="true" />
          </span>
        </Pill>
      </DocLink>
    </ToolTip>
  );
};

function getVersionTooltipText(version: string): string {
  const minVersion = version.replace("+", "");
  return `Requires Gadget framework version ${minVersion} or later. Click to learn more.`;
}
